import { EPSILON, PI2 } from "./miscellaneous";

/**
 * Asserts that a number is valid (finite and within safe integer range).
 *
 * @param value - The number to validate
 * @param subject - Custom error message for validation failure
 * @throws {Error} When the value is not finite or exceeds MAX_SAFE_INTEGER
 */
export function assertValidNumber(value: number, subject: string): void {
  if (!Number.isFinite(value)) {
    throw new Error(`${subject}: value must be a finite number`);
  }

  if (Math.abs(value) > Number.MAX_SAFE_INTEGER) {
    throw new Error(`${subject}: value exceeds maximum safe integer range`);
  }
}

/**
 * Asserts that an azimuth angle is valid (finite and between 0 and 2π).
 *
 * @param value - The azimuth angle in radians to validate
 * @param subject - Custom error message for validation failure
 * @throws {Error} When the value is not finite or is not between 0 and 2π radians
 */
export function assertValidAzimuth(value: number, subject: string): void {
  assertValidNumber(value, subject);

  if (value < 0 || value > PI2) {
    throw new Error(`${subject}: azimuth must be between 0 and 2π radians`);
  }
}

/**
 * Asserts that a number is within the unit range [0, 1].
 *
 * @param value - The number to validate
 * @param subject - Custom error message for validation failure
 * @throws {Error} When the value is not within the range [0, 1]
 */
export function assertValidUnitRange(value: number, subject: string): void {
  assertValidNumber(value, subject);

  if (value < 0 || value > 1) {
    throw new Error(`${subject}: value must be between 0 and 1`);
  }
}

/**
 * Asserts that a number is positive (greater than EPSILON).
 * Uses EPSILON to account for floating-point precision errors.
 *
 * @param value - The number to validate
 * @param subject - Custom error message for validation failure
 * @throws {Error} When the value is less than EPSILON
 */
export function assertValidPositiveNumber(
  value: number,
  subject: string,
): void {
  assertValidNumber(value, subject);

  if (value < EPSILON) {
    throw new Error(
      `${subject}: value must be greater than or equal to ${EPSILON}`,
    );
  }
}

/**
 * Asserts that a number is non-negative (greater than or equal to 0).
 *
 * @param value - The number to validate
 * @param subject - Custom error message for validation failure
 * @throws {Error} When the value is negative
 */
export function assertValidNonNegativeNumber(
  value: number,
  subject: string,
): void {
  assertValidNumber(value, subject);

  if (value < 0) {
    throw new Error(`${subject}: value must be greater than or equal to 0`);
  }
}
